import { useState } from "react"
import { GroupCredentialPanel } from "./GroupCredentialPanel"
import { PendingMemberReconcileRow, type PendingMember, type ReconcileCandidate } from "./PendingMemberReconcileRow"
import { StudentAssignmentPicker, type StudentOption } from "./StudentAssignmentPicker"
import { Toggle } from "../ui/Toggle"

type SeedbedGroup = {
  id: string
  name: string
  description?: string | null
  groupType: 'individual' | 'shared' | 'team' | 'edition'
  allowGroupLogin?: boolean
  credential?: { loginCode: string; isActive: boolean } | null
  students_count: number
  createdAt: string
}

type SeedbedStudentStatus = "active" | "suspended" | "ended"

type Enrollment = {
  id?: string
  userId: string
  status: SeedbedStudentStatus
  accessStartAt: string
  accessEndAt?: string | null
  assignedAt?: string
  user: { id?: string; name: string; email: string; studentProfile?: { institution?: string | null; documentNumber?: string | null } | null }
  group?: { id: string; name: string; groupType: string } | null
}

export type GroupStudentsViewProps = {
  group: SeedbedGroup | null
  seedbedId: string
  enrollments: Enrollment[]
  selectedStudents: StudentOption[]
  /** Student ids already enrolled in this seedbed (any group) — excluded from search. */
  excludeStudentIds: string[]
  updatingStudentStatusId: string | null
  savingGroupStudents: boolean
  onBack: () => void
  onToggleStudent: (student: StudentOption) => void
  onAssign: () => void
  onUpdateStatus: (userId: string, status: 'active' | 'suspended' | 'ended') => void
  enrollmentStatusBadge: (status: 'active' | 'suspended' | 'ended') => string
  toDateInput: (v?: string | null) => string
  pendingMembers: PendingMember[]
  reconcileCandidates: ReconcileCandidate[]
  onAddPendingMember: (name: string, email: string) => Promise<void> | void
  onReconcilePendingMember: (memberId: string, userId: string) => Promise<void> | void
  onToggleGroupLogin: (enabled: boolean) => Promise<void> | void
  togglingGroupLogin: boolean
}

export function GroupStudentsView({
  group, seedbedId, enrollments, selectedStudents, excludeStudentIds,
  updatingStudentStatusId, savingGroupStudents, onBack, onToggleStudent,
  onAssign, onUpdateStatus, enrollmentStatusBadge, toDateInput,
  pendingMembers, reconcileCandidates, onAddPendingMember, onReconcilePendingMember,
  onToggleGroupLogin, togglingGroupLogin,
}: GroupStudentsViewProps) {
  const [newMemberName, setNewMemberName] = useState("")
  const [newMemberEmail, setNewMemberEmail] = useState("")
  const [addingMember, setAddingMember] = useState(false)

  async function handleAddMember() {
    if (!newMemberName.trim() || !newMemberEmail.trim()) return
    setAddingMember(true)
    try {
      await onAddPendingMember(newMemberName.trim(), newMemberEmail.trim())
      setNewMemberName("")
      setNewMemberEmail("")
    } finally {
      setAddingMember(false)
    }
  }

  const isShared = group?.groupType === 'shared'
  const isTeam = group?.groupType === 'team'
  // Los grupos de equipo CCCD (cccd-seedbed-team-sync) también acumulan
  // SeedbedPendingMember — sin cuenta propia, sin login compartido — así que
  // necesitan ver esta sección tanto como los grupos de cuenta compartida.
  const showPendingMembers = isShared || isTeam

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button type="button" onClick={onBack}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400">
          ← Volver a grupos
        </button>
        <div>
          <h3 className="text-base font-semibold text-gray-800 dark:text-white/90">{group?.name ?? 'Grupo'}</h3>
          <span className={`text-xs font-medium ${isShared ? 'text-amber-600 dark:text-amber-400' : isTeam ? 'text-purple-600 dark:text-purple-400' : 'text-blue-600 dark:text-blue-400'}`}>
            {isShared ? 'Cuenta compartida' : isTeam ? 'Equipo CCCD' : 'Cuentas individuales'}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400"> · {enrollments.length} estudiante{enrollments.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {group && (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
          <Toggle
            checked={Boolean(group.allowGroupLogin)}
            disabled={togglingGroupLogin}
            onChange={(v) => void onToggleGroupLogin(v)}
            label="Login de equipo"
            description="Permite que todo el equipo entre con un solo usuario y contraseña (útil cuando no todos tienen cómo crear su propia cuenta, ej. se conectan desde el celular del profesor). Cada estudiante sigue acumulando su propio avance por separado."
          />

          {group.allowGroupLogin && (
            <GroupCredentialPanel
              seedbedId={seedbedId}
              groupId={group.id}
              existingLoginCode={group.credential?.isActive ? group.credential.loginCode : null}
            />
          )}
        </div>
      )}

      {showPendingMembers && group && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
          <h4 className="text-sm font-semibold text-gray-800 dark:text-white/90">Integrantes esperados</h4>
          <p className="text-xs text-gray-500">
            Regístralos por nombre y correo — no necesitan cuenta propia. Cuando exista o se cree una cuenta con ese
            correo, vincúlala aquí para trasladar su historial de avance.
          </p>

          {pendingMembers.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">Sin integrantes registrados aún.</p>
          ) : (
            <div className="space-y-2">
              {pendingMembers.map((m) => (
                <PendingMemberReconcileRow
                  key={m.id}
                  member={m}
                  candidates={reconcileCandidates}
                  onReconcile={onReconcilePendingMember}
                />
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100 dark:border-gray-800">
            <input placeholder="Nombre" value={newMemberName} onChange={(e) => setNewMemberName(e.target.value)}
              className="h-9 flex-1 min-w-[140px] rounded-lg border border-gray-300 px-3 text-sm dark:border-gray-700 dark:bg-gray-900" />
            <input placeholder="Email" type="email" value={newMemberEmail} onChange={(e) => setNewMemberEmail(e.target.value)}
              className="h-9 flex-1 min-w-[180px] rounded-lg border border-gray-300 px-3 text-sm dark:border-gray-700 dark:bg-gray-900" />
            <button type="button" disabled={addingMember} onClick={() => void handleAddMember()}
              className="rounded-lg bg-brand-500 px-4 py-2 text-xs font-medium text-white hover:bg-brand-600 disabled:opacity-60">
              {addingMember ? "Agregando..." : "Agregar"}
            </button>
          </div>
        </div>
      )}

      {enrollments.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 p-6 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">Sin estudiantes en este grupo aún.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Estudiante</th>
                <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Estado</th>
                <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Acceso</th>
                <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {enrollments.map((enrollment) => (
                <tr key={enrollment.userId} className="border-b border-gray-100 dark:border-gray-800">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900 dark:text-white">{enrollment.user.name}</p>
                    <p className="text-xs text-gray-500">{enrollment.user.email}</p>
                    {(enrollment.user.studentProfile?.institution || enrollment.user.studentProfile?.documentNumber) && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {[enrollment.user.studentProfile?.institution, enrollment.user.studentProfile?.documentNumber ? `Doc. ${enrollment.user.studentProfile.documentNumber}` : null]
                          .filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${enrollmentStatusBadge(enrollment.status as 'active' | 'suspended' | 'ended')}`}>
                      {enrollment.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {toDateInput(enrollment.accessStartAt)}{enrollment.accessEndAt ? ` → ${toDateInput(enrollment.accessEndAt)}` : ''}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      {enrollment.status !== 'active' && (
                        <button type="button" onClick={() => onUpdateStatus(enrollment.userId, 'active')}
                          disabled={updatingStudentStatusId === enrollment.userId}
                          className="text-xs text-green-600 hover:underline disabled:opacity-50">Activar</button>
                      )}
                      {enrollment.status === 'active' && (
                        <button type="button" onClick={() => onUpdateStatus(enrollment.userId, 'suspended')}
                          disabled={updatingStudentStatusId === enrollment.userId}
                          className="text-xs text-amber-600 hover:underline disabled:opacity-50">Suspender</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!isShared && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
          <h4 className="text-sm font-semibold text-gray-800 dark:text-white/90">Agregar estudiantes al grupo</h4>
          <StudentAssignmentPicker
            selectedStudents={selectedStudents}
            excludeIds={excludeStudentIds}
            onToggleStudent={onToggleStudent}
          />
          <button type="button" disabled={selectedStudents.length === 0 || savingGroupStudents} onClick={onAssign}
            className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60">
            {savingGroupStudents ? 'Asignando...' : `Asignar ${selectedStudents.length > 0 ? `${selectedStudents.length} estudiante${selectedStudents.length > 1 ? 's' : ''}` : 'estudiantes'} al grupo`}
          </button>
        </div>
      )}
    </div>
  )
}

export default GroupStudentsView
